import React, {useMemo, useState} from "react";
import {Box, ButtonBase, CircularProgress, InputBase, Menu, MenuItem, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Search01Icon, Tick02Icon} from "@hugeicons/core-free-icons";
import {useNavigate} from "react-router-dom";
import {ProjectRepository} from "../../repositories/ProjectRepository";

const TYPE_LABELS = {VOICE_ASSISTANT: "Voice", CHATBOT: "Chat"};

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();

// Project name in the header; opens a searchable list of the user's projects to switch to another one
export const ProjectSwitcher = ({project}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [projects, setProjects] = useState(null);
  const [search, setSearch] = useState("");

  const open = (event) => {
    setAnchorEl(event.currentTarget);
    setSearch("");
    // loaded on every open, so new projects show up
    ProjectRepository.getMy()
      .then(response => setProjects([...(response?.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
      .catch(e => {
        console.error(e);
        setProjects(current => current || []);
      });
  };

  const filtered = useMemo(() => (projects || []).filter(item =>
    [item.name, item.organizationName].some(value => (value || "").toLowerCase().includes(search.trim().toLowerCase()))
  ), [projects, search]);

  const select = (item) => {
    setAnchorEl(null);
    if (item.id !== project?.id) navigate(`/registry-project/${item.id}`);
  };

  return (
    <>
      <ButtonBase
        onClick={open}
        aria-label={"Switch project"}
        sx={{
          gap: 0.5, px: 0.75, height: 30, borderRadius: "8px", minWidth: 0,
          "&:hover": {bgcolor: "#F4F4FA"},
        }}
      >
        <Typography noWrap sx={{fontSize: 12, fontWeight: 600, color: "#23264D", maxWidth: {xs: 140, md: 280, lg: 380}}}>
          {project?.name}
        </Typography>
        <Box sx={{display: "flex", color: "#8A8AA0"}}><HugeiconsIcon icon={ArrowDown01Icon} size={15}/></Box>
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{sx: {borderRadius: "14px", mt: 0.75, width: 320, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
        MenuListProps={{sx: {py: 0.5}}}
        // keep typing in the search field instead of jumping to menu items
        autoFocus={false}
      >
        <Box sx={{px: 1.25, pt: 0.75, pb: 1}} onKeyDown={(e) => e.stopPropagation()}>
          <Typography sx={{px: 0.5, mb: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: "#8A8AA0"}}>
            SWITCH PROJECT
          </Typography>
          <Stack direction={"row"} alignItems={"center"} gap={1} sx={{
            height: 34, px: 1.25, borderRadius: "10px", border: "1px solid #E4E4EE", color: "#6B6B80",
            "&:focus-within": {borderColor: "#8491F7"},
          }}>
            <HugeiconsIcon icon={Search01Icon} size={15}/>
            <InputBase autoFocus placeholder={"Search projects"} value={search}
                       onChange={(e) => setSearch(e.target.value)} sx={{flex: 1, fontSize: 13}}/>
          </Stack>
        </Box>
        <Box sx={{maxHeight: 340, overflowY: "auto"}}>
          {projects === null && (
            <Stack alignItems={"center"} sx={{py: 2}}><CircularProgress size={20} sx={{color: "#8491F7"}}/></Stack>
          )}
          {projects !== null && filtered.length === 0 && (
            <Typography sx={{px: 2, py: 1.5, fontSize: 13, color: "#8A8AA0"}}>No projects found</Typography>
          )}
          {filtered.map(item => {
            const current = item.id === project?.id;
            return (
              <MenuItem key={item.id} onClick={() => select(item)} selected={current}
                        sx={{mx: 0.75, px: 1, py: 0.875, gap: 1.25, borderRadius: "10px"}}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)",
                }}>
                  {initials(item.name)}
                </Box>
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Typography noWrap sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{item.name}</Typography>
                  <Typography noWrap sx={{fontSize: 11, color: "#8A8AA0"}}>
                    {[TYPE_LABELS[item.type], item.organizationName,
                      item.subProjects?.length ? `${item.subProjects.length} language${item.subProjects.length > 1 ? "s" : ""}` : null]
                      .filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
                {current && <Box sx={{display: "flex", color: "#4B55C9"}}><HugeiconsIcon icon={Tick02Icon} size={16}/></Box>}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </>
  );
};
